import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'
import {
  FaAngleDoubleDown,
  FaAngleDoubleUp,
  FaChevronRight,
  FaEraser,
  FaSearch,
} from 'react-icons/fa'
import {
  buildDefaultExpanded,
  buildExpandMap,
  expandAllInTree,
  filterTreeByLabel,
} from '../../../utils/syllabusTreeUtils'
import {
  DUMMY_FILTER_TOPIC_TREE,
  FILTER_TREE_LEVELS,
} from './filterTopicTreeDataset'

function collectAllIds(nodes) {
  const ids = []
  const walk = (list) => {
    list.forEach((n) => {
      ids.push(n.id)
      if (n.children?.length) walk(n.children)
    })
  }
  walk(nodes)
  return ids
}

function collectLeafIds(nodes) {
  const ids = []
  const walk = (list) => {
    list.forEach((n) => {
      if (n.children?.length) walk(n.children)
      else ids.push(n.id)
    })
  }
  walk(nodes)
  return ids
}

function getSubtreeIds(node) {
  const ids = [node.id]
  if (node.children?.length) {
    node.children.forEach((c) => ids.push(...getSubtreeIds(c)))
  }
  return ids
}

/** @returns {'checked' | 'unchecked' | 'indeterminate'} */
function getCheckState(node, selected) {
  if (!node.children?.length) {
    return selected[node.id] ? 'checked' : 'unchecked'
  }
  const childStates = node.children.map((c) => getCheckState(c, selected))
  const allChecked = childStates.every((s) => s === 'checked')
  const allUnchecked = childStates.every((s) => s === 'unchecked')
  if (allChecked) return 'checked'
  if (allUnchecked) return 'unchecked'
  return 'indeterminate'
}

function countSelectedLeaves(nodes, selected) {
  let n = 0
  const walk = (list) => {
    list.forEach((node) => {
      if (node.children?.length) walk(node.children)
      else if (selected[node.id]) n += 1
    })
  }
  walk(nodes)
  return n
}

function allLeavesSelected(nodes, selected) {
  const leaves = collectLeafIds(nodes)
  return leaves.length > 0 && leaves.every((id) => selected[id])
}

function someLeafSelected(nodes, selected) {
  const leaves = collectLeafIds(nodes)
  return leaves.some((id) => selected[id])
}

function TreeItem({
  node,
  depth,
  expanded,
  onToggleExpand,
  selectedChapters,
  onToggleCheck,
  isTopLevel,
  levelLabels,
}) {
  const inputRef = useRef(null)
  const hasChildren = Boolean(node.children?.length)
  const isOpen = expanded[node.id]
  const checkState = getCheckState(node, selectedChapters)
  const levelLabel =
    levelLabels[Math.min(depth, levelLabels.length - 1)]

  useEffect(() => {
    const el = inputRef.current
    if (el) el.indeterminate = checkState === 'indeterminate'
  }, [checkState])

  const rowState =
    checkState === 'checked'
      ? 'is-on'
      : checkState === 'indeterminate'
        ? 'is-mixed'
        : 'is-off'

  return (
    <li
      role='none'
      className={`syllabus-panel__branch${isTopLevel ? ' syllabus-panel__branch--root' : ''}`}
    >
      <div
        className={`syllabus-panel__row ${rowState}`}
        style={{ '--depth': depth }}
        data-depth={depth}
        role='treeitem'
        aria-level={depth + 1}
        aria-expanded={hasChildren ? isOpen : undefined}
        aria-selected={checkState === 'checked'}
        aria-label={`${levelLabel}: ${node.label}`}
      >
        <div className='syllabus-panel__row-track' />
        <div className='syllabus-panel__row-body'>
          {hasChildren ? (
            <button
              type='button'
              className={`syllabus-panel__disclose${isOpen ? ' is-open' : ''}`}
              onClick={() => onToggleExpand(node.id)}
              aria-label={isOpen ? `Collapse ${node.label}` : `Expand ${node.label}`}
            >
              <FaChevronRight aria-hidden className='syllabus-panel__disclose-ico' />
            </button>
          ) : (
            <span className='syllabus-panel__disclose syllabus-panel__disclose--spacer' aria-hidden />
          )}
          <div className='syllabus-panel__choice custom-checkbox'>
            <input
              ref={inputRef}
              id={`syllabus-node-${node.id}`}
              type='checkbox'
              className='form-check-input'
              checked={checkState === 'checked'}
              onChange={() => onToggleCheck(node)}
            />
            <label className='syllabus-panel__label' htmlFor={`syllabus-node-${node.id}`}>
              <span className='syllabus-panel__label-text'>{node.label}</span>
              <span className='visually-hidden'>{` (${levelLabel})`}</span>
            </label>
          </div>
        </div>
      </div>
      {hasChildren && isOpen ? (
        <ul className='syllabus-panel__nest' role='group'>
          {node.children.map((child) => (
            <TreeItem
              key={child.id}
              node={child}
              depth={depth + 1}
              expanded={expanded}
              onToggleExpand={onToggleExpand}
              selectedChapters={selectedChapters}
              onToggleCheck={onToggleCheck}
              isTopLevel={false}
              levelLabels={levelLabels}
            />
          ))}
        </ul>
      ) : null}
    </li>
  )
}

/**
 * Syllabus filter: tree search, selection meter, bulk actions.
 * `levelLabels` should match syllabus depth (e.g. Topic → Subtopic → Chapter).
 */
function FilterTopicTree({
  treeData = DUMMY_FILTER_TOPIC_TREE,
  levelLabels = FILTER_TREE_LEVELS,
  selectedChapters,
  setSelectedChapters,
}) {
  const allIds = useMemo(() => collectAllIds(treeData), [treeData])
  const leafIds = useMemo(() => collectLeafIds(treeData), [treeData])
  const totalLeaves = leafIds.length

  const [searchQuery, setSearchQuery] = useState('')
  const [expanded, setExpanded] = useState(() =>
    buildDefaultExpanded(treeData, 2),
  )

  const visibleTree = useMemo(
    () => filterTreeByLabel(treeData, searchQuery),
    [treeData, searchQuery],
  )

  useEffect(() => {
    if (!searchQuery.trim()) {
      setExpanded(buildDefaultExpanded(treeData, 2))
    } else {
      setExpanded((prev) => ({
        ...prev,
        ...expandAllInTree(visibleTree, {}),
      }))
    }
  }, [searchQuery, treeData, visibleTree])

  const onToggleExpand = useCallback((id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }))
  }, [])

  const onToggleCheck = useCallback(
    (node) => {
      const subtree = getSubtreeIds(node)
      const state = getCheckState(node, selectedChapters)
      const turnOn = state !== 'checked'
      setSelectedChapters((prev) => {
        const next = { ...prev }
        subtree.forEach((id) => {
          next[id] = turnOn
        })
        return next
      })
    },
    [selectedChapters, setSelectedChapters],
  )

  const parentCheckboxRef = useRef(null)
  const allSel = allLeavesSelected(treeData, selectedChapters)
  const someSel =
    someLeafSelected(treeData, selectedChapters) && !allSel

  useEffect(() => {
    const el = parentCheckboxRef.current
    if (el) el.indeterminate = someSel
  }, [someSel])

  const leafCount = countSelectedLeaves(treeData, selectedChapters)
  const meterPct =
    totalLeaves > 0 ? Math.round((leafCount / totalLeaves) * 100) : 0

  const toggleSelectAllLeaves = () => {
    const next = !allSel
    setSelectedChapters((prev) => {
      const out = { ...prev }
      allIds.forEach((id) => {
        out[id] = next
      })
      return out
    })
  }

  const clearSelection = () => {
    setSelectedChapters((prev) => {
      const out = { ...prev }
      allIds.forEach((id) => {
        out[id] = false
      })
      return out
    })
    setExpanded(buildDefaultExpanded(treeData, 2))
    setSearchQuery('')
  }

  const expandAllBranches = () => {
    setExpanded(buildExpandMap(treeData, true))
  }

  const collapseAllBranches = () => {
    setExpanded(buildExpandMap(treeData, false))
  }

  const levelHint = levelLabels.join(' → ')

  return (
    <div className='syllabus-panel multiple-checkbox'>
      <header className='syllabus-panel__intro'>
        <div className='syllabus-panel__intro-text'>
          <h2 className='syllabus-panel__title'>Syllabus</h2>
          <p className='syllabus-panel__meta'>{levelHint}</p>
        </div>
        <div
          className='syllabus-panel__stat'
          title='Selected units vs total selectable units'
        >
          <span className='syllabus-panel__stat-num'>{leafCount}</span>
          <span className='syllabus-panel__stat-div'>/</span>
          <span className='syllabus-panel__stat-den'>{totalLeaves}</span>
        </div>
      </header>

      <div
        className='syllabus-panel__meter'
        role='progressbar'
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={meterPct}
        aria-label={`${leafCount} of ${totalLeaves} units selected`}
      >
        <div
          className='syllabus-panel__meter-fill'
          style={{ width: `${meterPct}%` }}
        />
      </div>

      <div className='syllabus-panel__search'>
        <FaSearch className='syllabus-panel__search-icon' aria-hidden />
        <input
          type='search'
          className='syllabus-panel__search-input'
          placeholder='Search units…'
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          aria-label='Search syllabus units'
          autoComplete='off'
        />
        {searchQuery ? (
          <button
            type='button'
            className='syllabus-panel__search-clear'
            onClick={() => setSearchQuery('')}
            aria-label='Clear search'
          >
            ×
          </button>
        ) : null}
      </div>

      <div className='syllabus-panel__bulk'>
        <div className='syllabus-panel__bulk-main'>
          <div className='form-check custom-checkbox syllabus-panel__bulk-check'>
            <input
              ref={parentCheckboxRef}
              id='syllabus-select-all'
              type='checkbox'
              className='form-check-input'
              checked={allSel}
              onChange={toggleSelectAllLeaves}
            />
            <label className='form-check-label mb-0' htmlFor='syllabus-select-all'>
              {allSel
                ? 'All units selected'
                : someSel
                  ? 'Some units selected'
                  : 'Select all units'}
            </label>
          </div>
        </div>
        <div
          className='syllabus-panel__bulk-actions'
          role='group'
          aria-label='Tree view actions'
        >
          <OverlayTrigger
            placement='top'
            overlay={
              <Tooltip id='syllabus-tooltip-expand'>Expand all branches</Tooltip>
            }
          >
            <button
              type='button'
              className='syllabus-panel__icon-btn'
              onClick={expandAllBranches}
              aria-label='Expand all branches'
            >
              <FaAngleDoubleDown aria-hidden className='syllabus-panel__icon-btn-ico' />
            </button>
          </OverlayTrigger>
          <OverlayTrigger
            placement='top'
            overlay={
              <Tooltip id='syllabus-tooltip-collapse'>Collapse all branches</Tooltip>
            }
          >
            <button
              type='button'
              className='syllabus-panel__icon-btn'
              onClick={collapseAllBranches}
              aria-label='Collapse all branches'
            >
              <FaAngleDoubleUp aria-hidden className='syllabus-panel__icon-btn-ico' />
            </button>
          </OverlayTrigger>
          <OverlayTrigger
            placement='top'
            overlay={
              <Tooltip id='syllabus-tooltip-clear'>
                Clear selection, search, and reset expansion
              </Tooltip>
            }
          >
            <button
              type='button'
              className='syllabus-panel__icon-btn syllabus-panel__icon-btn--danger'
              onClick={clearSelection}
              aria-label='Clear selection, search, and reset tree expansion'
            >
              <FaEraser aria-hidden className='syllabus-panel__icon-btn-ico' />
            </button>
          </OverlayTrigger>
        </div>
      </div>

      <div className='syllabus-panel__scroller checkbox-content-holder'>
        {visibleTree.length === 0 ? (
          <p className='syllabus-panel__empty' role='status'>
            {searchQuery.trim()
              ? `No matches for “${searchQuery.trim()}”.`
              : 'No syllabus data.'}
          </p>
        ) : (
          <ul className='syllabus-panel__tree' role='tree' aria-label='Syllabus hierarchy'>
            {visibleTree.map((node) => (
              <TreeItem
                key={node.id}
                node={node}
                depth={0}
                expanded={expanded}
                onToggleExpand={onToggleExpand}
                selectedChapters={selectedChapters}
                onToggleCheck={onToggleCheck}
                isTopLevel
                levelLabels={levelLabels}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

export default FilterTopicTree
